<template>
  <main class="product-units-page">
    <div class="product-units-page__body" ref="body">
      <div class="product-units-page__row">
        <h1 class="product-units-page__title">Единицы измерения</h1>
        <div class="product-units-page__buttons">
          <button
            class="product-units-page__buttons-add"
            type="button"
            v-on:click="openFormProductUnitDialog">
            Добавить единицу измерения
          </button>
        </div>
      </div>
      <product-unit-table
        v-bind:product-unit-list="productUnitList"
        v-bind:is-await-request="isAwaitRequest"
        v-bind:sort="sort"
        v-on:delete-product-unit="onDeleteProductUnit"
        v-on:edit-product-unit="fetchProductUnit"
        v-on:sort-product-unit-list="fetchProductUnitList"
      />
      <the-pagination v-model="page" v-bind:page-count="pageCount" />
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-bind:label="productUnitFormTitle"
        v-if="showFormProductUnitDialog"
        v-on:cancel="closeFormProductUnitDialog">
        <form-product-unit
          v-bind:input-errors="productUnitInputErrors"
          v-bind:unit-id="selectedProductUnit.id"
          v-bind:initial-name="selectedProductUnit.name"
          v-bind:initial-short-name="selectedProductUnit.shortName"
          v-bind:initial-note="selectedProductUnit.note"
          v-bind:is-await-request="isAwaitUnitRequest"
          v-on:submit="onSubmitProductUnit"
          v-on:close="closeFormProductUnitDialog" />
      </confirm-side-dialog>
    </side-slide-transition>
  </main>
</template>

<script>
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import notifierMixin from '@/mixins/notifierMixin';
import FormProductUnit from '@/components/Warehouse/FormProductUnit.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ProductUnitTable from '@/components/Warehouse/ProductUnitTable.vue';
import ThePagination from '@/components/ThePagination.vue';
import { ProductUnit } from '@/storage/gateways/ProductGateway';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';

/**
 * @description Страница единиц измерения товаров.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelWarehouseProductUnitList',
  components: {
    ConfirmSideDialog,
    FormProductUnit,
    SideSlideTransition,
    ProductUnitTable,
    ThePagination,
  },
  computed: {
    /**
     * @description Заголовок формы единицы измерения.
     * @returns {string}
     */
    productUnitFormTitle() {
      return this.selectedProductUnitId ? 'Редактировать единицу измерения' : 'Добавить единицу измерения';
    },
  },
  created() {
    this.fetchProductUnitList({ type: 'name', order: 'asc' });
  },
  data() {
    return {
      productUnitList: [],
      isAwaitRequest: false,
      isAwaitUnitRequest: false,
      sort: {},
      page: 1,
      pageCount: 0,
      selectedProductUnitId: '',
      selectedProductUnit: new ProductUnit(),
      productUnitInputErrors: {},
      showFormProductUnitDialog: false,
      isAwaitDownloadRequest: false,
    };
  },
  methods: {
    /**
     * @description Создать единицу измерения.
     * @param {ProductUnitRequest} request
     */
    createProductUnit(request) {
      this.isAwaitUnitRequest = true;
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.productUnitInputErrors = {};
        this.fetchProductUnitList(this.sort);
        this.closeFormProductUnitDialog();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.productUnitInputErrors = error.allCauses();
      };
      this
        .$productGateway
        .createProductUnit(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitUnitRequest = false; });
    },
    /**
     * @description Закрыть диалоговое окно формы единицы измерения.
     * @returns {void}
     */
    closeFormProductUnitDialog() {
      this.selectedProductUnitId = '';
      this.selectedProductUnit = new ProductUnit();
      this.showFormProductUnitDialog = false;
    },
    /**
     * @description Редактировать единицу измерения.
     * @param {ProductUnitRequest} request
     */
    editProductUnit(request) {
      this.isAwaitUnitRequest = true;
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeFormProductUnitDialog();
        this.productUnitInputErrors = {};
        this.fetchProductUnitList(this.sort);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.productUnitInputErrors = error.allCauses();
      };
      this
        .$productGateway
        .editProductUnit(request.getId(), request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitUnitRequest = false; });
    },
    /**
     * @description Открыть диалоговое окно формы единицы измерения.
     * @returns {void}
     */
    openFormProductUnitDialog() {
      this.showFormProductUnitDialog = true;
      this.productUnitInputErrors = {};
    },
    /**
     * @description Получить список единиц измерения.
     * @param {Object} sort Сортировка.
     * @returns {Promise<void>}
     */
    async fetchProductUnitList(sort) {
      this.sort = sort;
      const onSuccess = ({ productUnits, pageCount }) => {
        this.productUnitList = productUnits;
        this.pageCount = pageCount;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.isAwaitRequest = true;
      const either = await this.$productGateway.getProductUnitList(
        this.sort,
        this.page,
      );
      either.fold(onFail, onSuccess);
      this.isAwaitRequest = false;
    },
    /**
     * @description Получить единицу измерения.
     * @param {string} productUnitId Идентификатор единицы измерения.
     */
    async fetchProductUnit(productUnitId) {
      const onSuccess = ({ productUnit }) => {
        this.selectedProductUnitId = productUnitId;
        this.selectedProductUnit = productUnit;
        this.openFormProductUnitDialog();
      };
      const onFail = (error) => {
        this.selectedProductUnitId = '';
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .viewProductUnit(productUnitId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку запроса единицы измерения.
     * @param {ProductUnitRequest} request
     */
    onSubmitProductUnit(request) {
      if (request.getId()) {
        this.editProductUnit(request);
      } else {
        this.createProductUnit(request);
      }
    },
    /**
     * @description Обработать событие удаления единицу измерения.
     * @param {number} id Идентификатор единицы измерения.
     * @returns {void}
     */
    async onDeleteProductUnit(id) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить единицу измерения?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchProductUnitList(this.sort);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .deleteProductUnit(id)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
  ],
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";

.product-units {
  &-page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1fr;
      max-width: 250px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-add {
        @include buttons.btn-blue(100%, 48px, 16px);
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);

        &:before {
          content: "";
          background-image: url("@/assets/img/download-file-blue.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 20px;
          height: 20px;
          margin: 0 8px 0 0;
        }

        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        @include buttons.btn-blue(50%, 48px, 16px);
        width: 100%;
      }

      &-delete:disabled {
        background-color: var(--unfocus-color);
      }
    }

    &__body {
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: scroll;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    &__search {
      max-width: 400px;
    }
  }
}

@media screen and (max-width: 960px) {
  .product-units {
    &-page {
      flex-direction: column;

      &__row {
        flex-direction: column;
        gap: 10px;
      }

      &__search {
        max-width: 100%;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .product-units {
    &-page {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }

      &__buttons {
        grid-template-columns: 1.2fr 1fr;
        max-width: 100%;
        row-gap: 10px;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .product-units-page {
    &__buttons {
      grid-template-columns: minmax(200px, 230px);
      justify-content: left;
      &-export {
        display: none;
      }
      &-add {
        grid-column: auto;
      }
    }
  }
}
</style>
